import React, { useEffect, useState } from 'react';
import { api } from '../api';
import ConfirmModal from '../components/ConfirmModal';

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function Expenses() {
  const [tab, setTab] = useState('daily');

  // Daily expenses
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [newExpense, setNewExpense] = useState({ expense_title: '', expense_category: 'Miscellaneous', amount: '', payment_method: 'cash', notes: '', expense_date: today() });
  const [showConfirmExpense, setShowConfirmExpense] = useState(false);
  const [pendingExpenseId, setPendingExpenseId] = useState(null);

  // Staff
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [newStaff, setNewStaff] = useState({ full_name: '', role: 'Worker', phone_number: '', joining_date: today(), salary_type: 'Monthly', default_salary: 0 });
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ payment_amount: '', payment_date: today(), payment_type: 'Full Salary', payment_method: 'cash', notes: '' });
  const [showConfirmPayment, setShowConfirmPayment] = useState(false);
  const [pendingPaymentId, setPendingPaymentId] = useState(null);

  useEffect(() => { loadExpenses(); loadStaff(); }, []);

  const loadExpenses = async () => {
    setLoadingExpenses(true);
    try {
      const rows = await api.getExpenses();
      setExpenses(rows);
    } catch (e) {}
    setLoadingExpenses(false);
  };

  const saveExpense = async () => {
    if (!newExpense.expense_title || !newExpense.amount) return alert('Title and amount required');
    try {
      await api.createExpense({ ...newExpense, amount: Number(newExpense.amount) });
      setNewExpense({ expense_title: '', expense_category: 'Miscellaneous', amount: '', payment_method: 'cash', notes: '', expense_date: today() });
      loadExpenses();
    } catch (e) { alert(e.message); }
  };

  const deleteExpense = (id) => {
    setPendingExpenseId(id);
    setShowConfirmExpense(true);
  };

  const confirmDeleteExpense = async () => {
    if (!pendingExpenseId) return;
    try {
      await api.deleteExpense(pendingExpenseId);
      setShowConfirmExpense(false);
      setPendingExpenseId(null);
      loadExpenses();
    } catch (e) {
      setShowConfirmExpense(false);
    }
  };

  const loadStaff = async () => {
    setLoadingStaff(true);
    try { const rows = await api.getStaff(); setStaff(rows); } catch (e) {}
    setLoadingStaff(false);
  };

  const saveStaff = async () => {
    if (!newStaff.full_name || !newStaff.role) return alert('Name and role required');
    try {
      await api.createStaff({ ...newStaff });
      setNewStaff({ full_name: '', role: 'Worker', phone_number: '', joining_date: today(), salary_type: 'Monthly', default_salary: 0 });
      loadStaff();
    } catch (e) { alert(e.message); }
  };

  const selectStaff = async (s) => {
    setSelectedStaff(s);
    try {
      const resp = await api.getStaffPayments(s.id);
      setPayments(resp.payments || []);
    } catch (e) { setPayments([]); }
  };

  const savePayment = async () => {
    if (!selectedStaff) return alert('Select staff');
    if (!paymentForm.payment_amount) return alert('Amount required');
    try {
      await api.createStaffPayment(selectedStaff.id, { ...paymentForm, payment_amount: Number(paymentForm.payment_amount) });
      setPaymentForm({ payment_amount: '', payment_date: today(), payment_type: 'Full Salary', payment_method: 'cash', notes: '' });
      selectStaff(selectedStaff);
    } catch (e) { alert(e.message); }
  };

  const deletePayment = (id) => {
    setPendingPaymentId(id);
    setShowConfirmPayment(true);
  };

  const confirmDeletePayment = async () => {
    if (!pendingPaymentId) return;
    try {
      await api.deleteStaffPayment(pendingPaymentId);
      setShowConfirmPayment(false);
      setPendingPaymentId(null);
      if (selectedStaff) selectStaff(selectedStaff);
    } catch (e) {
      setShowConfirmPayment(false);
    }
  };

  const totalTodayExpenses = expenses.filter(e => e.expense_date === today()).reduce((s, e) => s + Number(e.amount), 0);
  const month = today().slice(0, 7);
  const totalMonthlyExpenses = expenses.filter(e => e.expense_date.slice(0, 7) === month).reduce((s, e) => s + Number(e.amount), 0);

  return (
    <div className="page-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div className="cat-tabs">
          {['daily', 'staff'].map(t => (
            <button key={t} className={`cat-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {t === 'daily' ? '💸 Daily Expenses' : '👷 Staff Payments'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'daily' && (
        <div className="two-col">
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 12, fontFamily: 'Rajdhani', fontSize: 16 }}>Add Expense</div>
            <div className="form-group">
              <label>Title</label>
              <input value={newExpense.expense_title} onChange={e => setNewExpense({ ...newExpense, expense_title: e.target.value })} placeholder="e.g. Gas cylinder" />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={newExpense.expense_category} onChange={e => setNewExpense({ ...newExpense, expense_category: e.target.value })}>
                {['Grocery', 'Utilities', 'Equipment', 'Maintenance', 'Miscellaneous'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Amount (₹)</label>
              <input type="number" min="0" step="0.01" value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>Payment Method</label>
              <select value={newExpense.payment_method} onChange={e => setNewExpense({ ...newExpense, payment_method: e.target.value })}>
                {['cash', 'upi', 'bank transfer', 'cheque'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={newExpense.expense_date} max={today()} onChange={e => setNewExpense({ ...newExpense, expense_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <input value={newExpense.notes} onChange={e => setNewExpense({ ...newExpense, notes: e.target.value })} placeholder="Optional notes" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={saveExpense}>Save Expense</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setNewExpense({ expense_title: '', expense_category: 'Miscellaneous', amount: '', payment_method: 'cash', notes: '', expense_date: today() })}>Clear</button>
            </div>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontFamily: 'Rajdhani', fontSize: 16 }}>Expenses</div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--iocl-muted)' }}>Today</div>
                <div style={{ fontWeight: 700, color: 'var(--iocl-orange)' }}>{fmt(totalTodayExpenses)}</div>
                <div style={{ fontSize: 11, color: 'var(--iocl-muted)', marginTop: 4 }}>This Month</div>
                <div style={{ fontWeight: 700, color: 'var(--iocl-orange)' }}>{fmt(totalMonthlyExpenses)}</div>
              </div>
            </div>
            {loadingExpenses ? (
              <div className="loading-center"><span className="spinner" /> Loading...</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Title</th><th>Category</th><th>Date</th><th>Amount</th><th>Method</th><th></th></tr>
                  </thead>
                  <tbody>
                    {expenses.length === 0 ? (
                      <tr><td colSpan={6}><div className="empty-state"><p>No expenses recorded</p></div></td></tr>
                    ) : expenses.map(e => (
                      <tr key={e.id}>
                        <td style={{ color: 'var(--iocl-white)' }}>{e.expense_title}</td>
                        <td><span className="badge badge-blue">{e.expense_category}</span></td>
                        <td>{e.expense_date}</td>
                        <td style={{ fontWeight: 700, color: 'var(--iocl-orange)' }}>{fmt(e.amount)}</td>
                        <td style={{ color: 'var(--iocl-muted)' }}>{e.payment_method}</td>
                        <td><button className="btn btn-danger btn-sm" onClick={() => deleteExpense(e.id)}>Delete</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'staff' && (
        <div className="two-col">
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 12, fontFamily: 'Rajdhani', fontSize: 16 }}>Add Staff</div>
            <div className="form-group">
              <label>Full Name</label>
              <input value={newStaff.full_name} onChange={e => setNewStaff({ ...newStaff, full_name: e.target.value })} placeholder="Staff member name" />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select value={newStaff.role} onChange={e => setNewStaff({ ...newStaff, role: e.target.value })}>
                {['Cook', 'Helper', 'Worker', 'Supervisor', 'Other'].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={newStaff.phone_number} onChange={e => setNewStaff({ ...newStaff, phone_number: e.target.value })} placeholder="Optional" />
            </div>
            <div className="form-group">
              <label>Salary Type</label>
              <select value={newStaff.salary_type} onChange={e => setNewStaff({ ...newStaff, salary_type: e.target.value })}>
                {['Monthly', 'Daily', 'Weekly'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Default Salary (₹)</label>
              <input type="number" min="0" value={newStaff.default_salary} onChange={e => setNewStaff({ ...newStaff, default_salary: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={saveStaff}>Save Staff</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setNewStaff({ full_name: '', role: 'Worker', phone_number: '', joining_date: today(), salary_type: 'Monthly', default_salary: 0 })}>Clear</button>
            </div>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontFamily: 'Rajdhani', fontSize: 16 }}>Staff & Payments</div>
              <span className="badge badge-blue">{staff.length} member{staff.length !== 1 ? 's' : ''}</span>
            </div>
            {loadingStaff ? (
              <div className="loading-center"><span className="spinner" /> Loading...</div>
            ) : (
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 200, minWidth: 160, borderRight: '1px solid var(--iocl-border)', paddingRight: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--iocl-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select staff</div>
                  <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                    {staff.length === 0 ? (
                      <div style={{ color: 'var(--iocl-muted)', fontSize: 12, padding: 8 }}>No staff added</div>
                    ) : staff.map(s => (
                      <div
                        key={s.id}
                        onClick={() => selectStaff(s)}
                        style={{
                          padding: '8px 6px',
                          cursor: 'pointer',
                          borderLeft: selectedStaff?.id === s.id ? '3px solid var(--iocl-orange)' : '3px solid transparent',
                          borderRadius: 4,
                          marginBottom: 2,
                        }}
                      >
                        <div style={{ fontWeight: 600, color: selectedStaff?.id === s.id ? 'var(--iocl-orange)' : 'var(--iocl-white)', fontSize: 13 }}>{s.full_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--iocl-muted)' }}>{s.role} · {s.phone_number || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ flex: 1 }}>
                  {selectedStaff ? (
                    <div>
                      <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--iocl-orange)' }}>{selectedStaff.full_name}</div>
                      <div style={{ marginBottom: 12, padding: 12, background: 'var(--iocl-panel)', borderRadius: 6 }}>
                        <div style={{ fontSize: 12, color: 'var(--iocl-muted)', marginBottom: 8 }}>Add Payment</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                          <div>
                            <label>Amount (₹)</label>
                            <input type="number" min="0" step="0.01" value={paymentForm.payment_amount} onChange={e => setPaymentForm({ ...paymentForm, payment_amount: e.target.value })} />
                          </div>
                          <div>
                            <label>Date</label>
                            <input type="date" value={paymentForm.payment_date} max={today()} onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
                          </div>
                          <div>
                            <label>Type</label>
                            <select value={paymentForm.payment_type} onChange={e => setPaymentForm({ ...paymentForm, payment_type: e.target.value })}>
                              {['Full Salary', 'Advance', 'Bonus', 'Partial'].map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label>Method</label>
                            <select value={paymentForm.payment_method} onChange={e => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}>
                              {['cash', 'upi', 'bank transfer'].map(m => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <label>Notes</label>
                          <input value={paymentForm.notes} onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })} placeholder="Optional" />
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={savePayment}>Add Payment</button>
                      </div>
                      <div className="table-wrap">
                        <table>
                          <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Method</th><th></th></tr></thead>
                          <tbody>
                            {payments.length === 0 ? (
                              <tr><td colSpan={5}><div className="empty-state" style={{ padding: 16 }}><p>No payments yet</p></div></td></tr>
                            ) : payments.map(p => (
                              <tr key={p.id}>
                                <td>{p.payment_date}</td>
                                <td><span className="badge badge-green">{p.payment_type}</span></td>
                                <td style={{ fontWeight: 700, color: 'var(--iocl-orange)' }}>{fmt(p.payment_amount)}</td>
                                <td style={{ color: 'var(--iocl-muted)' }}>{p.payment_method}</td>
                                <td><button className="btn btn-danger btn-sm" onClick={() => deletePayment(p.id)}>Delete</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state"><p>Select a staff member to view or add payments</p></div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirmExpense}
        title="Delete Expense"
        message="Are you sure you want to permanently delete this expense?"
        actionLabel="Delete Expense"
        actionColor="danger"
        onConfirm={confirmDeleteExpense}
        onCancel={() => setShowConfirmExpense(false)}
      />

      <ConfirmModal
        isOpen={showConfirmPayment}
        title="Delete Payment"
        message="Are you sure you want to permanently delete this payment record?"
        actionLabel="Delete Payment"
        actionColor="danger"
        onConfirm={confirmDeletePayment}
        onCancel={() => setShowConfirmPayment(false)}
      />
    </div>
  );
}
